import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { first, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { DestroyableBase } from '../../utils';
import { ContentViewModel } from '../carousel-item/content-view.model';
import { IconButtonComponent } from '../icon-button/icon-button.component';
import { AbstractMyListButtonService } from './abstract-my-list.service';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'osn-shared-icon-button[osnMyList]',
  exportAs: 'osnMyList',
})
export class MyListButtonDirective extends DestroyableBase implements OnInit, OnChanges {
  private readonly programAvailabilityIdUrlChanged$ = new Subject<void>();

  private hasView = false;
  private playHoverAnimations = false;

  @Input() public programAvailabilityIdUrl: string;
  @Input() public title: string;
  @Input() public content: ContentViewModel;
  @Input() public itemGuid: string;
  @Input() public reflectStateInIconText = true;
  @HostBinding('disabled') public isLoading: boolean;

  private readonly isAddedToWatchlistSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly isAddedToWatchlist$: Observable<boolean> = this.isAddedToWatchlistSubject$.asObservable();

  @HostListener('click') public onClick() {
    this.playHoverAnimations = false;
    if (this.isAddedToWatchlistSubject$.getValue()) {
      this.hostComponent.lottieAnimationOptions = {
        path: 'assets/animations/animation-remove-to-add.json',
        loop: false,
      };
      this.service.removeFromMyList(this.content);
    } else {
      this.hostComponent.lottieAnimationOptions = {
        path: 'assets/animations/animation-add-to-check.json',
        loop: false,
      };
      this.service.addToMyList(this.content);
    }
  }

  @HostListener('mouseenter') public onMouseEnter() {
    if (!this.isLoading && this.isAddedToWatchlistSubject$.getValue()) {
      this.playHoverAnimations = true;
      this.hostComponent.lottieAnimationOptions = {
        path: 'assets/animations/animation-check-to-remove.json',
        loop: false,
      };
      if (this.reflectStateInIconText) {
        this.hostComponent.buttonText = this.translateService.instant('MyList_Button_RemoveFromMyList');
      }
    }
  }

  @HostListener('mouseleave') public onMouseLeave() {
    if (!this.isLoading && this.isAddedToWatchlistSubject$.getValue() && this.playHoverAnimations) {
      this.playHoverAnimations = false;
      this.ngZone.runOutsideAngular(() => {
        this.hostComponent.animationItem?.setDirection(-1);
        this.hostComponent.animationItem.play();
      });
      if (this.reflectStateInIconText) {
        this.hostComponent.buttonText = this.translateService.instant('MyList_Button_Added');
      }
    }
  }

  constructor(
    private readonly hostComponent: IconButtonComponent,
    private readonly elementRef: ElementRef,
    private readonly service: AbstractMyListButtonService,
    private readonly ref: ChangeDetectorRef,
    private readonly renderer: Renderer2,
    private readonly translateService: TranslateService,
    private readonly ngZone: NgZone
  ) {
    super();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.programAvailabilityIdUrl || changes.reflectStateInIconText) {
      this.programAvailabilityIdUrlChanged$.next();
    }
  }

  public ngOnInit() {
    this.programAvailabilityIdUrlChanged$
      .pipe(
        takeUntil(this.onDestroy$),
        startWith(undefined),
        switchMap(() => this.service.isAddedToMyList$(this.content))
      )
      .subscribe(this.isAddedToWatchlistSubject$);

    // set the starting state of the lottie animations
    this.isAddedToWatchlist$.pipe(first()).subscribe((isAddedToWatchlist) => {
      if (isAddedToWatchlist) {
        this.hostComponent.lottieAnimationOptions = {
          path: 'assets/animations/animation-check-to-remove.json',
          loop: false,
          autoplay: false,
        };
      } else {
        this.hostComponent.lottieAnimationOptions = {
          path: 'assets/animations/animation-add-to-check.json',
          loop: false,
          autoplay: false,
        };
      }
    });

    combineLatest([
      this.translateService.stream('MyList_Button_Added'),
      this.translateService.stream('MyList_Button_AddToMyList'),
      this.isAddedToWatchlistSubject$,
    ])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(([titleRemoveFromMyList, titleAddToMyList, isAddedToMyList]) => {
        if (this.reflectStateInIconText) {
          this.hostComponent.buttonText = isAddedToMyList ? titleRemoveFromMyList : titleAddToMyList;
        } else {
          this.hostComponent.buttonText = undefined;
        }
        this.ref.detectChanges();
      });

    this.service
      .isLoading$(this.itemGuid)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((isLoading) => {
        this.isLoading = isLoading;
        this.ref.markForCheck();
      });

    this.service.isUserLoggedIn$.pipe(takeUntil(this.onDestroy$)).subscribe((isLoggedIn) => {
      if (isLoggedIn && !this.hasView) {
        this.renderer.setStyle(this.elementRef.nativeElement, 'display', 'flex');
        this.hasView = true;
      } else {
        this.renderer.setStyle(this.elementRef.nativeElement, 'display', 'none');
        this.hasView = false;
      }
    });
  }
}
